import dispatchActionForAllModules from '@utils/dispatch-action-for-all-modules'
import Vue from 'vue'
import Vuex from 'vuex'
import toast from '@services/toasts/index.js'
import api from '@/plugins/api.storePlugin'
import i18n from '@/plugins/i18n.storePlugin'
import appInsights from '@/plugins/appinsights.storePlugin'
import { getServiceStatus } from '@/helpers/service-status-helper.js'
import { isCacheFresh } from '@/helpers/cache-helpers'
import { DurationUnits } from '@/shared/constants/date/DurationUnits.js'
import { success } from '@/helpers/result-helper.js'
import dayjs from '@/services/date'
import { logger } from '@/services/logging/AppLogger'
import modules from './modules'

Vue.use(Vuex)

const getDefaultState = () => {
  return {
    debugMessages: [],
    loadingCount: 0,
    appLoadingCount: 0,
    debugActivateCounter: 0,
    darkMode: JSON.parse(localStorage.getItem('darkMode')),
    initialAppLoad: false, // Prevents full page loader on subsequent loads
    embedded: false, // App is embedded within RR
    drawer:
      localStorage.getItem('drawer') === undefined ||
      localStorage.getItem('drawer') === null
        ? false
        : JSON.parse(localStorage.getItem('drawer')),
    serviceStatus: null,
    serviceStatusLoadingCount: 0,
  }
}

const state = getDefaultState()

const store = new Vuex.Store({
  modules,
  state,
  plugins: [api, i18n, appInsights],
  // Enable strict mode in development to get a warning
  // when mutating state outside of a mutation.
  // https://vuex.vuejs.org/guide/strict.html
  strict: process.env.NODE_ENV !== 'production',
  getters: {
    moduleName: () => 'root-store',
    isLoading: (state) => state.loadingCount > 0,
    isLoadingApp: (state) => state.appLoadingCount > 0,
    serviceStatus: (state) => {
      if (!state.serviceStatus || !state.serviceStatus?.html) return null
      return state.serviceStatus
    },
    isLoadingServiceStatus: (state) => state.serviceStatusLoadingCount > 0,
    hasLoadedAppOnce: (state) => state.initialAppLoad,
    isError: (state) => state.error,
    isDebugModeActive: (state) => state.debugActivateCounter >= 10,
    isDarkModeActive: (state) => state.darkMode,
    isDrawerOpen: (state) => state.drawer,
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_SERVICE_STATUS_LOADING(state) {
      state.serviceStatusLoadingCount++
    },
    FINISH_SERVICE_STATUS_LOADING(state) {
      state.serviceStatusLoadingCount--
    },
    INCREMENT_APP_LOADING(state) {
      state.appLoadingCount++
    },
    DECREMENT_APP_LOADING(state) {
      state.appLoadingCount--
    },
    SET_APP_AS_LOADED(state) {
      state.initialAppLoad = true
    },
    RESET_APP_LOADING_STATE(state) {
      state.initialAppLoad = false
    },
    SET_ERROR(state) {
      state.error = true
    },
    ACTIVATE_DEBUG(state) {
      state.debugActivateCounter = 10
    },
    INCREMENT_DEBUG(state) {
      state.debugActivateCounter++
    },
    RESET_DEBUG(state) {
      state.debugActivateCounter = 0
    },
    ENABLE_DARKMODE(state) {
      state.darkMode = true
      localStorage.setItem('darkMode', true)
    },
    DISABLE_DARKMODE(state) {
      state.darkMode = false
      localStorage.setItem('darkMode', false)
    },
    OPEN_DRAWER(state) {
      state.drawer = true
      localStorage.setItem('drawer', true)
    },
    CLOSE_DRAWER(state) {
      state.drawer = false
      localStorage.setItem('drawer', false)
    },
    CLEAR(state) {
      // Clear out local and session storage
      localStorage.clear()
      sessionStorage.clear()

      Object.assign(state, getDefaultState())
    },
    ADD_DEBUG_MESSAGE(state, obj) {
      state.debugMessages.push(obj)
    },
    INSERT_SERVICE_STATUS(state, serviceStatus) {
      state.serviceStatus = {
        ...{
          lastUpdated: dayjs(),
        },
        ...serviceStatus,
      }
    },
  },
  actions: {
    addDebugMessage({ commit, getters }, message) {
      if (getters.isDebugModeActive) {
        commit('ADD_DEBUG_MESSAGE', { date: new Date(), message: message })
        toast.debug(message)
        console.log(message)
      }
    },
    startLoadingApp({ commit }) {
      commit('INCREMENT_APP_LOADING')
    },
    finishLoadingApp({ commit }) {
      commit('DECREMENT_APP_LOADING')
    },
    startLoading({ commit }) {
      commit('START_LOADING')
    },
    finishLoading({ commit }) {
      commit('FINISH_LOADING')
    },
    setAppAsLoaded({ commit }) {
      commit('SET_APP_AS_LOADED')
    },
    resetAppLoadingState({ commit }) {
      commit('RESET_APP_LOADING_STATE')
    },
    toggleDebugMode({ commit, dispatch }) {
      if (this.state.debugActivateCounter <= 0) {
        commit('ACTIVATE_DEBUG')
        dispatch('addDebugMessage', 'Debug mode on')
      } else {
        dispatch('addDebugMessage', 'Debug mode off')
        commit('RESET_DEBUG')
      }
    },
    toggleDarkMode({ commit }) {
      this.state.darkMode
        ? commit('DISABLE_DARKMODE')
        : commit('ENABLE_DARKMODE')
    },
    toggleDrawer({ commit }) {
      this.state.drawer ? commit('CLOSE_DRAWER') : commit('OPEN_DRAWER')
    },
    clearStore({ commit }) {
      dispatchActionForAllModules('clear')

      commit('CLEAR')
    },
    async setLocale({ dispatch }, locale) {
      this.$i18n.locale = locale
      await dispatch('setFavicon')
    },
    setFavicon(context) {
      const favicon = document.querySelector("link[rel~='icon']")
      favicon.href = this.$i18n.t('app.favicon')
    },
    async fetchServiceStatus({ commit, getters }, forceRefresh) {
      // 1. Check data is cached
      if (
        isCacheFresh({
          cacheDuration: 1,
          durationUnits: DurationUnits.HOUR,
          lastUpdated: state.serviceStatus?.lastUpdated,
          forceRefresh,
        })
      )
        return success({ data: getters.serviceStatus })

      // 2. Fetch fresh data
      commit('START_SERVICE_STATUS_LOADING')

      const response = await getServiceStatus()

      commit('INSERT_SERVICE_STATUS', response.isSuccess ? response.data : null)

      commit('FINISH_SERVICE_STATUS_LOADING')
      return response
    },
    /**
     * Logs store exceptions via the app logger
     * @param {*} param0
     * @param {StoreErrorDTO} payload
     */
    logStoreException(context, payload) {
      logger.logError(payload)
    },
  },
})

export default store

// Automatically run the `init` action for every module,
// if one exists.
dispatchActionForAllModules('init')
